import { colorSchemeOptionListAtom } from 'features/generator/contentFormSections/client/ContentDesignForm'
import { atom } from 'jotai'

import {
	primaryColorAtom,
	secondaryColorAtom,
	selectedColorSchemeAtom,
} from '@/content-from-sections/client'
import { TUploadedMedia } from '@/services'
import { getFileFromMedia, isOnServer } from '@/utils'

import {
	appButtonListAtom,
	appDescriptionAtom,
	appDeveloperAtom,
	appNameAtom,
	appPlatformLinksAtom,
	TQrColorScheme,
	welcomeImageAtom,
	welcomeImageFileAtom,
} from '../../contentFormSections'
import {
	imageFormAtom,
	imageFormFileAtom,
} from '../../contentFormSections/ImageForm'
import { QR_TYPE } from '../../generatorTypes'

export type TAppContent = {
	type: QR_TYPE.App
	colors: Omit<TQrColorScheme, 'name'>
	appInfo: {
		name: string
		developer: string
		description: string
		buttons: { title: string; url: string | undefined }[]
		logo: TUploadedMedia
	}
	platformLinks: { name: string; url: string }[]
	loaderImage: TUploadedMedia
}

export const appContentAtom = atom<TAppContent, [TAppContent], void>(
	(get) => ({
		type: QR_TYPE.App,
		colors: {
			primary: get(primaryColorAtom),
			secondary: get(secondaryColorAtom),
		},
		platformLinks: get(appPlatformLinksAtom) || [],
		appInfo: {
			logo:
				get(imageFormAtom) ||
				({
					publicId: '',
					resourceType: '',
					format: '',
					bytes: 0,
				} as TUploadedMedia),
			name: get(appNameAtom) || '',
			developer: get(appDeveloperAtom) || '',
			description: get(appDescriptionAtom) || '',
			buttons: get(appButtonListAtom) || [],
		},
		loaderImage:
			get(welcomeImageAtom) ||
			({
				publicId: '',
				resourceType: '',
				format: '',
				bytes: 0,
			} as TUploadedMedia),
	}),
	(get, set, content: TAppContent) => {
		// Color scheme
		const colorScheme = get(colorSchemeOptionListAtom).find(
			(scheme) =>
				scheme.primary === content.colors.primary &&
				scheme.secondary === content.colors.secondary,
		)

		set(selectedColorSchemeAtom, colorScheme?.name ?? '')
		set(primaryColorAtom, content.colors.primary)
		set(secondaryColorAtom, content.colors.secondary)

		set(imageFormAtom, content.appInfo.logo)
		if (!isOnServer && content.appInfo.logo?.publicId) {
			queueMicrotask(async () => {
				try {
					const logoFile = await getFileFromMedia(content.appInfo.logo)
					if (get(imageFormAtom)?.publicId === content.appInfo.logo.publicId) {
						set(imageFormFileAtom, logoFile)
					}
				} catch (error) {
					console.error('Failed to load logo file:', error)
				}
			})
		}
		set(appButtonListAtom, content.appInfo.buttons)
		set(appNameAtom, content.appInfo.name)
		set(appDeveloperAtom, content.appInfo.developer)
		set(appDescriptionAtom, content.appInfo.description)
		set(appPlatformLinksAtom, content.platformLinks)

		set(welcomeImageAtom, content.loaderImage)
		if (!isOnServer && content.loaderImage?.publicId) {
			queueMicrotask(async () => {
				try {
					const loaderFile = await getFileFromMedia(content.loaderImage)
					if (
						get(welcomeImageAtom)?.publicId === content.loaderImage.publicId
					) {
						set(welcomeImageFileAtom, loaderFile)
					}
				} catch (error) {
					console.error('Failed to load loader image file:', error)
				}
			})
		}
	},
)
